// Shared markdown + citation rendering helpers for chat surfaces.
// Used by ChatWidget (live streaming popup) and SessionThread (chat-history
// page replay + composer). Behaviour kept identical so a saved session looks
// the same in both places.

import {
  Children, cloneElement, isValidElement,
  type ReactNode, type ReactElement,
} from 'react';
import type ReactMarkdown from 'react-markdown';
import { CitationPill } from '../components/CitationPill';
import { PeoplePill } from '../components/PeoplePill';
import { BillButton } from '../components/BillButton';
import { safeNavUrl } from './safeUrl';
import { useT } from '../i18n';
import type { Citation, LinkablePerson } from './innoq';

/** Bill-number shape, e.g. "25.026" / "22.3041". */
const BILL_NUMBER_RE = /^\d{2}\.\d{3,4}$/;

// ─── Pre-processing ──────────────────────────────────────────────────────────

/** Strip internal API markers and convert LaTeX delimiters for remark-math. */
export function preprocessLatex(text: string): string {
  return text
    .replace(/gpt_trainer_ai_function_x_call\s*/g, '')
    .replace(/\\\n/g, '\n\n')
    .replace(/\\\[(.+?)\\\]/gs, (_, g) => `$$${g}$$`)
    .replace(/\\\((.+?)\\\)/g,  (_, g) => `$${g}$`);
}

// Disable single-dollar math to prevent false positives (e.g. prices like $10)
export const remarkMathOptions = { singleDollarTextMath: false };

/** Trim, collapse excess blank lines, convert single newlines to markdown hard breaks. */
export function sanitizeUserText(val: string): string {
  return val.trim().replace(/\n{2,}/g, '\n').replace(/\n/g, '  \n');
}

/**
 * While streaming, markdown tokens arrive incomplete (e.g. "**bold" before the
 * closing "**"). This temporarily closes any unclosed markers so ReactMarkdown
 * can render partial text correctly on every chunk.
 */
export function healStreamingMarkdown(text: string): string {
  if (!text) return text;
  let result = text;

  const fenceCount = (result.match(/^```/gm) || []).length;
  if (fenceCount % 2 !== 0) {
    result += '\n```';
    return result;
  }

  const inlineCodeCount = (result.match(/(?<!`)`(?!`)/g) || []).length;
  if (inlineCodeCount % 2 !== 0) result += '`';

  const boldCount = (result.match(/\*\*/g) || []).length;
  if (boldCount % 2 !== 0) result += '**';

  const singleStarCount = (result.replace(/\*\*/g, '').match(/\*/g) || []).length;
  if (singleStarCount % 2 !== 0) result += '*';

  const isTableRow = (l: string) => /^\s*\|.+\|/.test(l);
  const isSepRow   = (l: string) => /^\s*\|?(\s*:?-+:?\s*\|)+\s*$/.test(l);
  const lines = result.split('\n');
  const patched: string[] = [];
  let injected = false;
  let tableHasSep = false;
  for (let i = 0; i < lines.length; i++) {
    patched.push(lines[i]);
    if (!isTableRow(lines[i]) && !isSepRow(lines[i])) { tableHasSep = false; continue; }
    if (isSepRow(lines[i])) { tableHasSep = true; continue; }
    if (!tableHasSep) {
      const next = lines[i + 1];
      const needsSep = next === undefined || next.trim() === '' || (isTableRow(next) && !isSepRow(next));
      if (needsSep) {
        const cols = lines[i].split('|').filter(c => c.trim() !== '').length;
        patched.push('| ' + Array(Math.max(cols, 1)).fill('---').join(' | ') + ' |');
        injected = true;
        tableHasSep = true;
      }
    }
  }
  if (injected) result = patched.join('\n');
  return result;
}

// ─── Citation injection ──────────────────────────────────────────────────────

// Single combined scan token. Group 1 = a `[N]` / `[N, M, …]` councillor·speech
// marker (digit list). Group 2 = a bare affair number (YY.NNN / YY.NNNN) that
// is content-anchored to its bill link. Scanning both in one pass keeps prose
// order when a sentence mixes the two (e.g. "Rösti [1] filed 26.443").
const MARKER_OR_BILL_RE = /\[(\d+(?:\s*,\s*\d+)*)\]|(\b\d{2}\.\d{3,4}\b)/g;

// Split the backend `citations` array into the two resolution mechanisms:
//   • billByRef — affair/dispatch entries keyed by their bare number (id === 0).
//     Resolved by the NUMBER the model wrote, so a bill link can never point at
//     the wrong entity ("26.443" only ever resolves to affair 26.443).
//   • pillById  — councillor/speech entries keyed by their `[N]` id (id >= 1).
function citationMaps(citations: Citation[]) {
  const billByRef = new Map<string, Citation>();
  const pillById = new Map<number, Citation>();
  for (const c of citations ?? []) {
    if ((c.type === 'affair' || c.type === 'dispatch') && c.ref) {
      if (!billByRef.has(c.ref)) billByRef.set(c.ref, c);
    } else if (typeof c.id === 'number' && c.id >= 1) {
      pillById.set(c.id, c);
    }
  }
  return { billByRef, pillById };
}

// Orphan `[N]` markers (no matching `citations[]` entry from enrich_response)
// used to render as a visible red bullet, which made dropped citations look
// like a styled bug. Render NOTHING instead — the surrounding identifier
// (name, affair number, etc.) is still visible; we just don't visually
// promise a click-through that doesn't exist. Logged once per render so
// regressions still surface in dev.
function CitationPlaceholder({ id }: { id: number }) {
  if (typeof window !== 'undefined' && (import.meta as any).env?.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[chat] orphan citation marker [${id}] — text references it but enrich_response.citations has no matching entry`);
  }
  return null;
}

// ─── People-by-name safety net ───────────────────────────────────────────────
//
// The backend ships EVERY councillor the turn gathered ({name, url}) on a
// side-channel (see innoq `LinkablePerson`). We match each FULL name in the
// rendered answer text and wrap EVERY unlinked occurrence in a PeoplePill, so a
// councillor the model named but forgot to cite ([N]) still links to their
// profile — including when the same name appears in both an intro sentence and a
// later table row. Strict bounds (see buildPeopleMatcher / withCitations) keep
// this from ever cross-linking, matching a bare surname, or nesting inside an
// existing link.

/** Escape a string for safe use inside a RegExp. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Order-independent identity key for a full name: lowercased, diacritics
 * stripped, whitespace-split, sorted. So "Christian Lohr" and "Lohr Christian"
 * share one key — used for the ambiguity guard, so two DISTINCT people whose
 * names are anagrams-by-token can never cross-link under order-insensitive
 * matching (both are dropped).
 */
function nameKey(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .sort()
    .join(' ');
}

/** All permutations of a small token array (caller bounds length to keep n! tiny). */
function permutations(arr: string[]): string[][] {
  if (arr.length <= 1) return [arr];
  const out: string[][] = [];
  for (let i = 0; i < arr.length; i++) {
    const rest = [...arr.slice(0, i), ...arr.slice(i + 1)];
    for (const p of permutations(rest)) out.push([arr[i], ...p]);
  }
  return out;
}

/**
 * Build an ORDER-INSENSITIVE full-name matcher: all tokens must appear as an
 * adjacent run in ANY order (so "Lohr Christian" matches the canonical
 * "Christian Lohr"). Word-boundary + Unicode aware, with a \b fallback for
 * engines without lookbehind. For 2–4 tokens every permutation is enumerated
 * (≤24 alternatives); ≥5-token names fall back to fixed order to bound blowup.
 */
function peopleNameRegex(name: string): RegExp {
  const tokens = name.split(/\s+/).filter(Boolean).map(escapeRegExp);
  const body =
    tokens.length >= 2 && tokens.length <= 4
      ? '(?:' + permutations(tokens).map((p) => p.join('\\s+')).join('|') + ')'
      : tokens.join('\\s+');
  try {
    return new RegExp(`(?<![\\p{L}\\p{N}_])${body}(?![\\p{L}\\p{N}_])`, 'iu');
  } catch {
    return new RegExp(`\\b${body}\\b`, 'i');
  }
}

interface PeopleMatcher {
  /** Longest-name-first so a longer full name wins over a contained shorter one. */
  entries: { name: string; url: string; portrait?: string | null; re: RegExp }[];
}

/**
 * Build the name→url matcher from the turn's gathered councillors.
 *
 * Safety bounds enforced HERE:
 *   • Full names only — a name must have ≥2 whitespace-separated tokens, so a
 *     bare surname is never matched (two councillors sharing a surname can't
 *     cross-link).
 *   • Ambiguity — if two DISTINCT profile URLs share one full name, that name is
 *     dropped entirely (skip both) rather than guessed.
 *   • Word-boundary, case-insensitive, Unicode-aware matching (so "Müller",
 *     "Céline Amaudruz", "Jean-Luc Addor" match cleanly; falls back to \b on
 *     engines without lookbehind).
 *
 * Returns null when nothing is matchable, so callers can skip the pass entirely.
 */
function buildPeopleMatcher(people?: LinkablePerson[]): PeopleMatcher | null {
  if (!people || people.length === 0) return null;
  // Keyed by the ORDER-INSENSITIVE token-set key (nameKey), so "Christian Lohr"
  // and "Lohr Christian" are one identity for the ambiguity guard.
  const urlsByKey = new Map<string, Set<string>>();
  const firstByKey = new Map<string, { name: string; url: string; portrait?: string | null }>();
  for (const p of people) {
    if (!p || typeof p.name !== 'string' || typeof p.url !== 'string') continue;
    const name = p.name.trim();
    const url = p.url.trim();
    if (!name || !url) continue;
    // Full name only — reject single-token names so a bare surname never links.
    if (name.split(/\s+/).filter(Boolean).length < 2) continue;
    const key = nameKey(name);
    let set = urlsByKey.get(key);
    if (!set) { set = new Set(); urlsByKey.set(key, set); }
    set.add(url);
    if (!firstByKey.has(key)) {
      firstByKey.set(key, { name, url, portrait: typeof p.portrait === 'string' ? p.portrait : null });
    }
  }
  const entries: PeopleMatcher['entries'] = [];
  for (const [key, urls] of urlsByKey) {
    if (urls.size !== 1) continue; // ambiguous same-token-set full name → skip both
    const { name, url, portrait } = firstByKey.get(key)!;
    entries.push({ name, url, portrait, re: peopleNameRegex(name) });
  }
  entries.sort((a, b) => b.name.length - a.name.length);
  return entries.length ? { entries } : null;
}

/**
 * Push `text` into `parts`, wrapping EVERY councillor full name found in a
 * PeoplePill — not just the first occurrence. A name that appears in an intro
 * sentence AND again in a table row is linked in both places. Text with no match
 * passes through unchanged. Only ever called on the PLAIN-TEXT slices of the
 * citation scan — never inside an already-linked span (the `insideLink` guard in
 * withCitations skips this pass there), so a pill can never nest inside a link.
 *
 * On each pass we pick the earliest match across all names (longest wins on a
 * tie), emit it, then advance past it and re-scan the remainder — so the same
 * name can match again later in the same slice. Progress is guaranteed because
 * `rest` always shrinks past the consumed match; `guard` only bounds a
 * pathological many-name segment.
 */
function pushPeopleText(
  parts: ReactNode[],
  text: string,
  matcher: PeopleMatcher | null,
  keySeed: string,
): void {
  if (!matcher || !text) { if (text) parts.push(text); return; }
  let rest = text;
  let consumed = 0;
  for (let guard = 0; guard < 256 && rest; guard++) {
    let best: { idx: number; len: number; url: string; portrait?: string | null } | null = null;
    for (const e of matcher.entries) {
      const m = e.re.exec(rest); // non-global → always scans from 0 of `rest`
      if (!m) continue;
      if (best === null || m.index < best.idx || (m.index === best.idx && m[0].length > best.len)) {
        best = { idx: m.index, len: m[0].length, url: e.url, portrait: e.portrait };
      }
    }
    if (!best) break;
    const before = rest.slice(0, best.idx);
    const matched = rest.slice(best.idx, best.idx + best.len);
    if (before) parts.push(before);
    const safe = safeNavUrl(best.url);
    if (safe) {
      parts.push(<PeoplePill key={`person-${keySeed}-${consumed + best.idx}`} name={matched} url={best.url} portrait={best.portrait ?? null} />);
    } else {
      parts.push(matched); // unsafe url → plain text
    }
    consumed += best.idx + best.len;
    rest = rest.slice(best.idx + best.len);
  }
  if (rest) parts.push(rest);
}

// `billButtons` controls how an affair number renders: a rounded bill-number
// button in prose, a compact inline link inside tables (where the number sits
// in its own column and a full chip per row would be heavy).
//
// `people` is the councillor name→url matcher (built once per message render in
// mdComponents and shared across every element; it is stateless, so every
// occurrence of a name links). `insideLink` is set when recursing into an <a> so
// a name inside an existing markdown link is NEVER double-linked.
export function withCitations(
  children: ReactNode,
  citations: Citation[],
  billButtons = true,
  people: PeopleMatcher | null = null,
  insideLink = false,
): ReactNode {
  const { billByRef, pillById } = citationMaps(citations);
  const emitText = (t: string, seed: string, parts: ReactNode[]) => {
    if (insideLink) { if (t) parts.push(t); }
    else pushPeopleText(parts, t, people, seed);
  };
  return Children.map(children, (child) => {
    if (typeof child === 'string') {
      const parts: ReactNode[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      MARKER_OR_BILL_RE.lastIndex = 0;
      while ((match = MARKER_OR_BILL_RE.exec(child)) !== null) {
        const preceding = match.index > lastIndex ? child.slice(lastIndex, match.index) : '';
        if (preceding) emitText(preceding, `pre-${match.index}`, parts);

        if (match[2] !== undefined) {
          // Bare affair number → content-anchored bill link. The number itself
          // is the key, so this can never resolve to the wrong entity. A number
          // not in this turn's data simply stays plain text (no false link).
          const num = match[2];
          const bill = billByRef.get(num);
          const safe = bill ? safeNavUrl(bill.url) : null;
          if (bill && safe) {
            parts.push(
              billButtons ? (
                <BillButton
                  key={`bill-${num}-${match.index}`}
                  number={num}
                  url={bill.url}
                  title={bill.label}
                  kind={bill.type === 'dispatch' ? 'dispatch' : 'affair'}
                />
              ) : (
                <a
                  key={`bill-${num}-${match.index}`}
                  href={safe}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  title={bill.label}
                  className="text-[var(--accent)] underline underline-offset-2 font-medium hover:opacity-75"
                >
                  {num}
                </a>
              ),
            );
          } else {
            parts.push(num); // unknown / unsafe → plain text
          }
        } else {
          // `[N]` marker → councillor / speech pill only. Affair brackets and
          // orphans render nothing: affairs are linked via their number above,
          // so a stray or misnumbered bracket can never produce a wrong pill.
          const ids = match[1]
            .split(',')
            .map((s) => parseInt(s.trim(), 10))
            .filter((n) => Number.isFinite(n));
          ids.forEach((id, idx) => {
            const cite = pillById.get(id);
            if (cite) {
              parts.push(<CitationPill key={`cite-${id}-${match!.index}-${idx}`} citation={cite} />);
            } else {
              parts.push(<CitationPlaceholder key={`cite-ph-${id}-${match!.index}-${idx}`} id={id} />);
            }
          });
        }
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < child.length) emitText(child.slice(lastIndex), `tail-${lastIndex}`, parts);
      return parts.length > 1 ? <>{parts}</> : (parts[0] ?? child);
    }
    if (isValidElement(child)) {
      const el = child as ReactElement<{ children?: ReactNode }>;
      // Recurse — but if this element is an <a> (a markdown link), suppress the
      // people-name pass inside it so a name already inside a link is never
      // wrapped in a second link.
      const childInsideLink = insideLink || el.type === 'a';
      return cloneElement(el, {}, withCitations(el.props.children, citations, billButtons, people, childInsideLink));
    }
    return child;
  });
}

// ─── Markdown component overrides ────────────────────────────────────────────
// Bound to the message's citations so [N] markers in any text-bearing element
// turn into interactive citation pills.

export function mdComponents(
  citations: Citation[],
  linkablePeople?: LinkablePerson[],
): React.ComponentProps<typeof ReactMarkdown>['components'] {
  // Built once per render and shared across every element override (cheap regex
  // reuse). The matcher is stateless, so a councillor named in multiple blocks
  // links in every one.
  const people = buildPeopleMatcher(linkablePeople);
  return {
    p: ({ children, node }) => {
      const child = (node as any)?.children?.[0];
      if ((node as any)?.children?.length === 1 && child?.tagName === 'img') {
        return <>{children}</>;
      }
      return <p className="mb-2 last:mb-0 leading-[1.65]">{withCitations(children, citations, true, people)}</p>;
    },
    a: ({ href, children }) => {
      const label = Array.isArray(children) ? String(children[0]) : String(children ?? '');
      // Reject any href whose protocol isn't safe. Model-generated content
      // can otherwise smuggle `javascript:` / `vbscript:` / `data:text/html`
      // links that browsers still execute on click.
      const isSafeNav = (u: string | undefined): boolean =>
        !!u && /^(?:https?:|mailto:|tel:|\/|#)/i.test(u);
      const isSafeMedia = (u: string | undefined): boolean =>
        !!u && /^https?:/i.test(u);

      if (label === 'image' && isSafeMedia(href)) {
        return (
          <div className="w-fit my-2">
            <img src={href} alt="" className="max-h-[250px] rounded-2xl object-contain aspect-auto transition-opacity duration-300" />
          </div>
        );
      }
      if (label === 'video' && isSafeMedia(href)) {
        return <video src={href} controls className="max-h-[250px] rounded-2xl my-2 w-full" />;
      }
      // A bill the model emitted as an inline link (e.g. `[25.026](url)`) —
      // render the number as a rounded bill button rather than an underline,
      // matching the affair-citation rendering above.
      if (isSafeNav(href) && BILL_NUMBER_RE.test(label.trim())) {
        return <BillButton number={label.trim()} url={href!} title={label} />;
      }
      const safeHref = isSafeNav(href) ? href : '#';
      return (
        <a href={safeHref} target="_blank" rel="noreferrer"
           onClick={e => e.stopPropagation()}
           className="text-[var(--accent)] underline underline-offset-2 font-semibold hover:opacity-75 break-words">
          {children}
        </a>
      );
    },
    strong: ({ children }) => <strong className="font-semibold text-inherit">{withCitations(children, citations, true, people)}</strong>,
    em:     ({ children }) => <em className="italic text-inherit">{withCitations(children, citations, true, people)}</em>,
    pre: ({ children }) => (
      <pre className="rounded-[var(--r2)] p-3 overflow-x-auto text-[12px] font-mono my-3 whitespace-pre-wrap shadow-sm"
           style={{ background: '#1A1917', color: '#F0EDE8' }}>
        {children}
      </pre>
    ),
    code: ({ className, children, ...props }) => {
      const isBlock = /language-/.test(className ?? '');
      return isBlock
        ? <code className={`${className ?? ''} text-inherit`} {...props}>{children}</code>
        : <code className="bg-black/10 rounded px-1 py-0.5 text-[12px] font-mono text-inherit break-words" {...props}>{children}</code>;
    },
    img: ({ src, alt }) => (
      <div className="w-fit my-2">
        <img src={src} alt={alt ?? ''} className="max-h-[250px] rounded-2xl object-contain aspect-auto transition-opacity duration-300" />
      </div>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto my-3 rounded-[var(--r2)] border border-[var(--border)]">
        <table className="w-full text-[12px] border-collapse">{children}</table>
      </div>
    ),
    th: ({ children }) => (
      <th className="px-3 py-2 text-left font-semibold text-[var(--text-1)] border-b border-[var(--border)] whitespace-nowrap bg-[var(--surface)]">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-3 py-2 text-[var(--text-2)] border-b border-[var(--border)]/40 last:border-b-0">
        {/* Affair numbers in a table's Number column render as bill BUTTONS,
            same as in prose. (The old compact-link treatment dates from when a
            separate Ref/[N] column sat beside the Number column and a button
            would have duplicated it; affairs now link by their number, so there
            is no duplicate column to avoid.) */}
        {withCitations(children, citations, true, people)}
      </td>
    ),
    tr: ({ children, ...props }) => (
      <tr className="even:bg-black/[0.02]" {...props}>{children}</tr>
    ),
    ul: ({ children }) => <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>,
    li: ({ children }) => <li className="leading-[1.65]">{withCitations(children, citations, true, people)}</li>,
    // Heading scale sits clearly above the 13px assistant body so section
    // headers read as headings, not just bold text. Practical range the model
    // emits: h2 (top section — Lead / Evidence / Context) → h3 (sub-block —
    // Dossier / Vote Record) → h4 (minor). h1 is styled largest in case it
    // appears, but section headers should top out at h2.
    h1: ({ children }) => <h1 className="text-[21px] font-bold mt-4 mb-2 text-[var(--text-1)] border-b border-[var(--border)] pb-1">{withCitations(children, citations, true, people)}</h1>,
    h2: ({ children }) => <h2 className="text-[18px] font-bold mt-4 mb-2 text-[var(--text-1)]">{withCitations(children, citations, true, people)}</h2>,
    h3: ({ children }) => <h3 className="text-[15px] font-semibold mt-3 mb-1 text-[var(--text-1)]">{withCitations(children, citations, true, people)}</h3>,
    h4: ({ children }) => <h4 className="text-[13.5px] font-semibold mt-3 mb-1 text-[var(--text-2)]">{withCitations(children, citations, true, people)}</h4>,
    hr: () => <hr className="my-4 border-[var(--border)]" />,
    blockquote: ({ children }) => (
      <blockquote className="border-l-[3px] border-[var(--accent)] pl-3 my-3 text-[var(--text-3)] italic">
        {withCitations(children, citations, true, people)}
      </blockquote>
    ),
  };
}

// ─── Misc UI bits ────────────────────────────────────────────────────────────

export function Cursor() {
  return (
    <span
      className="inline-block w-[2px] h-[14px] bg-current ml-0.5 align-middle"
      style={{ animation: 'blink 1s step-end infinite' }}
    />
  );
}

export function ThinkingIndicator() {
  const t = useT();
  return (
    <div className="px-3 py-2">
      <span className="thinking-shimmer text-[13px] font-mono italic font-semibold tracking-wide">
        {t('chat.thinking', 'Thinking')}
      </span>
    </div>
  );
}
